import {
  get,
  isArray,
  isEqual,
  isNil,
  isNull,
  map,
  snakeCase,
  toString,
} from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { getArticleTypeInGA } from '../../common/analytics/helpers/articleTypeHelper';
import { ADP } from '../../common/analytics/sourceTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import {
  HTTP_RESPONSE_CODES,
  MAX_CONTENT_HEIGHT,
} from '../../common/helpers/constants';
import {
  postCategory,
  getMainCategory,
  getSubcategories,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import { isVideoPost, VideoType } from '../../common/helpers/domain/videoTypes';
import logger from '../../common/helpers/logger/logger';
import UrlHelper from '../../common/helpers/routing/UrlHelper';
import { isDesktop } from '../../common/helpers/utility/screenHelper';
import Author from '../../components/aboutAuthor/AboutAuthor';
import HalfPageAds from '../../components/ads/HalfPageAds';
import { BackToTop } from '../../components/backToTop/BackToTop';
import CommentContainer from '../../components/commentNew/CommentContainer';
import ContentBreadcrumb from '../../components/contentPage/contentBreadcrumb/ContentBreadcrumb';
import ContentSchema from '../../components/contentPage/contentSchema/ContentSchema';
import VideoSchema from '../../components/contentPage/contentSchema/VideoSchema';
import ContentSeoHeader from '../../components/contentPage/contentSeoHeader/ContentSeoHeader';
import ContentTitle from '../../components/contentPage/contentTitle/ContentTitle';
import PostContent from '../../components/contentPage/postContent/PostContent';
import RelatedProjectInformation from '../../components/contentPage/projectInformation/RelatedProjectInformation';
import ProjectRelatedArticle from '../../components/contentPage/projectRelatedArticle/ProjectRelatedArticle';
import Recommended from '../../components/contentPage/recommended/Recommended';
import HeroImage from '../../components/heroImage/HeroImage';
import { LeadsCapture } from '../../components/leadsCapture/LeadsCapture';
import {
  ArticlePageLeadsCaptureWrapper,
  BillboardAdWrapper,
  ContentLeft,
  ContentLeftWrapper,
  ContentMainWrapper,
  ContentRightWrapper,
  DisclaimerContainer,
  Divider,
  InnerPageWrapper,
  ProjectModuleContent,
  ProjectModuleWrapper,
  ShareLabelWrapper,
  ShareWrapper,
} from '../../components/pages/contentPage/ContentPage.style';
import {
  DynamicComponentForOtherPreview,
  DynamicComponentForWhatToRead,
} from '../../components/pages/contentPage/DynamicComponents';
import { LatestProjectFooter } from '../../components/latestProjectReviewFooter/LatestProjectReviewFooter';
import ShareItems from '../../components/share/ShareItems';
import Tags from '../../components/tags/Tags';
import Page from '../../layouts/main';
import ContentText from '../../locales/th/content.json';
import {
  fetchOtherPreviewPosts,
  fetchPostDetails,
  fetchReadNextPosts,
  useCountPostViewMutation,
} from '../../services/post';
import { fetchProjectArticles } from '../../services/project';
import { fetchRecommendedVideos } from '../../services/video';
import { trackArticleSubscribeButton } from '../../common/analytics/analyticsTracker';
import { updateCurrentArticle } from '../../store/content/slice';
import { updateCurrentProject } from '../../store/project/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import Error from '../_error';
import { resolved } from '../../common/helpers/utility/promiseHelper';
import { Disclaimer } from '../../components/disclaimer/Disclaimer';
import {
  getAdSectByPageType,
  getAdSubsectByPropertyType,
} from '../../common/helpers/ads/AdsHelper';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';
import { getPropertyTypeFromUrlSegment } from '../../common/helpers/domain/propertyTypes';
import { shouldShowLeadCaptureForm } from '../../common/helpers/utility/LeadsCaptureHelper';
import { getProjectNetProjectHref } from '../../common/helpers/routing/urlGenerator';
import { fetchProjectData } from '../../services/projectnetProject';
import {
  TransformedProjectNetDataType,
  transformProjectnetProjectData,
} from '../../common/helpers/data/transformProjectnetProjectData';
import { WholeState } from '../../store/common/type';
import {
  Post,
  PostDetailQueryData,
} from '../../queries/contentPage/postDetail';
import { NextPage } from 'next';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import { RecommendedData } from '../../common/helpers/domain/recommendation';
import { OtherPreviewData } from '../../common/helpers/domain/otherPreview';
import { AnalyticsProject } from '../../common/analytics/interfaces/analyticsProject';
import { ProjectArticleData } from '../../queries/projectPage/projectArticles';
import { fetchSeoMetaComposerData } from '../../services/metaComposer';
import { SeoMetaComposerData } from '../../common/helpers/domain/seoMetaComposerData';
import { prepareParamsForSeoMetaComposerRequest } from '../../common/helpers/utility/prepareParams';
import Faq from '../../components/contentPage/faq/Faq';

// Importing ads components dynamically to avoid loading them in the server side rendering
const Billboard = dynamic(() => import('../../components/ads/Billboard'), {
  ssr: false,
});

const HALF_AD_INTERVAL_HEIGHT = 1800;
const HALF_AD_HEIGHT = 600;
const HALF_AD_MAX_NUMBER = 3;
const HALF_AD_LOAD_TIMEOUT = 1000;
const GET_AD_NUMBER_IN_CONTENT_TIMEOUT = 1000;

interface Author {
  name: string;
}

interface ContentPageProps {
  asPath: string;
  canonicalLink: string;
  post: Post;
  projectRelatedPostData?: ProjectArticleData;
  postOtherPreviewData?: OtherPreviewData;
  postRecommendedData?: RecommendedData;
  postWhatToReadData?: { posts: { items: Post[] } };
  projectnetProjectData?: TransformedProjectNetDataType;
  projectNetProjectHref?: string;
  seoMetaComposerData?: SeoMetaComposerData;
}
type PostInfo = Post;
const isPointOfView = (type: string) => isEqual(postCategory.pointOfView, type);
const seoSchema = (videoType: VideoType, post: PostInfo) => {
  if (isVideoPost(videoType)) {
    return <VideoSchema post={post} />;
  } else {
    return <ContentSchema post={post} />;
  }
};

const ContentPage: NextPage<ContentPageProps> = ({
  asPath,
  canonicalLink,
  post,
  projectRelatedPostData,
  postOtherPreviewData,
  postRecommendedData,
  postWhatToReadData,
  projectnetProjectData,
  projectNetProjectHref,
  seoMetaComposerData,
}) => {
  const [halfAds, setHalfAds] = useState([]);
  const [halfAdsCount, setHalfAdsCount] = useState(0);
  const [triggered, setTriggered] = useState(false);
  const [postContentHeight, setPostContentHeight] = useState(0);
  const postContentRef = useRef(null);
  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );
  const postId = post?.id;

  const leadCaptureForm = shouldShowLeadCaptureForm(post);
  const [countViewPage] = useCountPostViewMutation();
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  useEffect(() => {
    if (Number.isInteger(postId)) {
      countViewPage({
        variables: { id: postId },
      }).catch(() => {
        logger.logError(
          'ContentPage',
          'Error putting article page view count',
          { postId },
        );
      });
    }
  }, [countViewPage, postId]);

  const generateHalfAds = (isFirstLoad?: number | boolean) => {
    const actualHalfAds = [];

    if (isDesktop(deviceType)) {
      const postSectionHeight = postContentRef.current
        ? postContentRef.current.clientHeight
        : 0;
      const contentHeight = isFirstLoad
        ? Math.min(MAX_CONTENT_HEIGHT, postSectionHeight)
        : postSectionHeight;
      const adsNumber = Math.min(
        HALF_AD_MAX_NUMBER,
        calculateHalfAdsCount(contentHeight),
      );
      setHalfAdsCount(adsNumber);

      for (let i = 0; i < adsNumber; i++) {
        actualHalfAds.push({
          medrec: `${AdUnits.AP_Medrec}` + `${i + 1}`,
        });
      }
    }
    setHalfAds(actualHalfAds);
  };
  const calculatePostContentHeight = () => {
    setTimeout(() => {
      const postSectionHeight = postContentRef.current
        ? postContentRef.current.clientHeight
        : 0;
      setPostContentHeight(postSectionHeight);
    }, GET_AD_NUMBER_IN_CONTENT_TIMEOUT);
  };

  const handleShowHalfAds = () => {
    setTimeout(() => {
      generateHalfAds();
      calculatePostContentHeight();
    }, HALF_AD_LOAD_TIMEOUT);
  };

  useEffect(() => {
    if (!triggered && config.isBrowser) {
      generateHalfAds(true);
      setTriggered(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, postContentRef, triggered]);

  const calculateHalfAdsCount = (contentHeight: number) => {
    if (contentHeight < HALF_AD_HEIGHT + HALF_AD_INTERVAL_HEIGHT) {
      return 1;
    } else {
      let adsNumber = Math.floor(
        contentHeight / (HALF_AD_HEIGHT + HALF_AD_INTERVAL_HEIGHT),
      );
      const leftHeight =
        contentHeight % (HALF_AD_HEIGHT + HALF_AD_INTERVAL_HEIGHT);
      adsNumber += leftHeight >= HALF_AD_HEIGHT ? 1 : 0;
      return adsNumber;
    }
  };

  if (!post) {
    return <Error statusCode={HTTP_RESPONSE_CODES.NotFound} />;
  }

  const {
    embedVideo,
    author = {
      total_post: '',
      description: '',
      profileImage: {
        url: '',
        title: '',
        width: '',
        height: '',
      },
      name: '',
      link: '',
    },
    tags = [],
    articleHeroImage = {
      url: '',
      title: '',
    },
    project,
    videoType,
    latestReviewPost,
  } = post;
  let { postCategory } = post;

  const { profileImage } = author;
  postCategory = snakeCase(postCategory) as PostSubcategory;

  const desktopBillboardAdHeight = 250 + 32;
  const adContainerHeight = HALF_AD_INTERVAL_HEIGHT + HALF_AD_HEIGHT;
  const lastAdContainerHeight =
    postContentHeight - (halfAdsCount - 1) * adContainerHeight;

  const getPropertyTypesSect = (post: Post) => {
    const propertyTypesSect: string[] = [];

    post?.propertyType?.map((propertyType) => {
      propertyTypesSect.push(
        getAdSubsectByPropertyType(getPropertyTypeFromUrlSegment(propertyType)),
      );
    });

    return propertyTypesSect;
  };

  return (
    <Page source={ADP.PAGE}>
      {seoSchema(videoType, post)}
      <ContentSeoHeader
        post={post}
        canonicalLink={canonicalLink}
        seoMetaComposerData={seoMetaComposerData}
      />
      <ContentBreadcrumb post={post} />
      {isPointOfView(postCategory) && (
        <HeroImage heroImageData={articleHeroImage} />
      )}

      <ContentMainWrapper>
        {/* Left Content */}
        <ContentLeftWrapper
          className={'content-left-section'}
          data-test={'content-left-section'}
        >
          <ContentLeft>
            <ContentTitle postingData={post} asPath={asPath} />
            {isVideoPost(videoType) && (
              <p
                className="post-video"
                dangerouslySetInnerHTML={{ __html: embedVideo }}
              />
            )}
            <div ref={postContentRef}>
              <PostContent
                sect={
                  post?.propertyType.length > 0
                    ? getPropertyTypesSect(post)
                    : getAdSectByPageType(LandingPageTypes.ArticlePage)
                }
                subSect={postCategory}
                postingData={post}
                onHeightChange={() => {
                  handleShowHalfAds();
                  setTimeout(() => {
                    // Trigger a scroll event to show the half ads
                    window.dispatchEvent(new CustomEvent('scroll'));
                  }, 3000);
                }}
                showLeadCaptureForm={leadCaptureForm}
              />
            </div>
            <>
              {leadCaptureForm && (
                <div id="leads_form_section">
                  <ArticlePageLeadsCaptureWrapper>
                    <LeadsCapture
                      leadCaptureFormId={post.project.leadCaptureFormId}
                      projectId={post.project.id}
                      onClickRegisterButton={() =>
                        trackArticleSubscribeButton(
                          userInfo,
                          project,
                          post,
                          ADP.PAGE,
                        )
                      }
                    />
                  </ArticlePageLeadsCaptureWrapper>
                </div>
              )}
              <ShareWrapper deviceType={deviceType}>
                <ShareLabelWrapper>
                  {ContentText.share.shareArticle}
                </ShareLabelWrapper>
                <ShareItems asPath={asPath} deviceType={deviceType} />
              </ShareWrapper>
              {project.id && project.link && (
                <ProjectModuleWrapper>
                  <ProjectModuleContent>
                    {projectnetProjectData && (
                      <RelatedProjectInformation
                        heroImageData={projectnetProjectData?.heroImage}
                        project={projectnetProjectData as AnalyticsProject}
                        lazyLoad={true}
                        projectNetProjectHref={projectNetProjectHref}
                      />
                    )}
                    <ProjectRelatedArticle
                      project={project}
                      post={post}
                      lazyLoad={true}
                      projectRelatedPostData={projectRelatedPostData}
                    />
                  </ProjectModuleContent>
                </ProjectModuleWrapper>
              )}
              <Tags postingDataTags={tags} />
              <Author
                authorData={author}
                authorImageProfile={profileImage}
                shouldApplyLazyload={true}
              />
              <CommentContainer
                postId={post.id}
                postData={post}
                source={ADP.PAGE}
                isOnProjectPage={false}
              />
              {seoMetaComposerData &&
                seoMetaComposerData[0]?.tags?.faqs?.length > 0 && (
                  <Faq faqData={seoMetaComposerData[0]?.tags?.faqs} />
                )}
            </>
          </ContentLeft>
        </ContentLeftWrapper>

        {/* Right Content */}
        {isDesktop(deviceType) && (
          <ContentRightWrapper
            className={'content-right'}
            data-test={'content-right-section'}
          >
            {!isPointOfView(postCategory) && (
              <Recommended
                isOnContentPage
                postRecommendedData={postRecommendedData}
              />
            )}
            {config.isBrowser && (
              <>
                {map(halfAds, (halfAd, i) => (
                  <React.Fragment key={i}>
                    <HalfPageAds
                      adUnit={get(halfAd, 'medrec')}
                      sect={
                        post?.propertyType.length > 0
                          ? getPropertyTypesSect(post)
                          : getAdSectByPageType(LandingPageTypes.ArticlePage)
                      }
                      subSect={postCategory}
                      project={project}
                      articleId={postId}
                      adContainerHeight={
                        i !== halfAdsCount - 1
                          ? adContainerHeight
                          : lastAdContainerHeight
                      }
                    />
                  </React.Fragment>
                ))}
              </>
            )}
          </ContentRightWrapper>
        )}

        {/* Post Ads */}
        <React.Fragment>
          {isDesktop(deviceType) && (
            <BillboardAdWrapper adHeight={desktopBillboardAdHeight}>
              <Billboard
                isOnContentPage
                adUnit={AdUnits.AP_Billboard1}
                sect={
                  post?.propertyType.length > 0
                    ? getPropertyTypesSect(post)
                    : getAdSectByPageType(LandingPageTypes.ArticlePage)
                }
                subSect={postCategory}
                project={project}
                articleId={postId}
              />
            </BillboardAdWrapper>
          )}
        </React.Fragment>
      </ContentMainWrapper>

      {postOtherPreviewData?.otherPreview?.length > 0 && (
        <>
          <Divider />
          <DynamicComponentForOtherPreview
            postingData={post}
            id={postId}
            source={ADP.PAGE}
            postOtherPreviewData={postOtherPreviewData}
          />
          <Divider />
        </>
      )}
      <InnerPageWrapper>
        <DynamicComponentForWhatToRead
          id={postId}
          sect={
            post?.propertyType.length > 0
              ? getPropertyTypesSect(post)
              : getAdSectByPageType(LandingPageTypes.ArticlePage)
          }
          subSect={postCategory}
          project={get(post, 'project', {})}
          source={ADP.PAGE}
          postingData={post}
          lazyLoad={true}
          postWhatToReadData={postWhatToReadData}
        />
      </InnerPageWrapper>
      <Divider />
      <DisclaimerContainer>
        <Disclaimer />
      </DisclaimerContainer>
      <BackToTop />
      <LatestProjectFooter {...latestReviewPost} />
    </Page>
  );
};

ContentPage.getInitialProps = async ({
  store,
  query,
  res,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<ContentPageProps> => {
  const { params } = query;
  const canonicalLink = config.baseUrl + asPath.substring(1);

  // Extracting article id
  const articleId = UrlHelper.parsePath(params as string)?.id;
  let postData: PostDetailQueryData = null;
  let projectRelatedPostData: ProjectArticleData = null;
  let postOtherPreviewData;
  let postRecommendedData = {};
  let postWhatToReadData;
  let projectnetProjectData: TransformedProjectNetDataType = null;
  let projectNetProjectHref = null;
  let seoMetaComposerData = null;
  let seoMetaComposerRequestData = null;

  // Business requirement: If post category is location insight then retrieve by main category (which is all subcategories of the main category)
  const getPostCategoryParams = (postCategory: PostSubcategory) => {
    return postCategory === 'location_insight'
      ? getSubcategories(getMainCategory(postCategory))
      : postCategory;
  };

  store.dispatch(updateCurrentSourcePage(ADP.PAGE));

  // When we have a valid article id
  if (articleId && isFinite(parseInt(articleId))) {
    const variables = {
      articleId: parseInt(articleId),
    };

    postData = await fetchPostDetails(apolloClient, variables);

    if (postData?.post) {
      const {
        categories,
        tags,
        id,
        title,
        postCategory,
        project,
        videoType,
        link,
        status,
        projectNetPropertyId,
      } = postData.post;

      let { propertyType } = postData.post;
      const articleTypeInGA = getArticleTypeInGA(postCategory, videoType);
      if (projectNetPropertyId && !isNull(projectNetPropertyId)) {
        try {
          // Fetch and transform project data from ProjectNet
          const projectnetProject =
            await fetchProjectData(projectNetPropertyId);
          projectnetProjectData =
            transformProjectnetProjectData(projectnetProject);

          // Normalize propertyType
          if (!Array.isArray(propertyType)) {
            propertyType = [propertyType];
          }

          // Prepare project URL
          projectNetProjectHref = getProjectNetProjectHref(
            propertyType[0],
            projectNetPropertyId,
          );

          // Prepare data for SEO Meta Composer request
          const postCategory = getPostCategoryParams(
            postData.post.postCategory,
          );
          const postCategoryNormalized = Array.isArray(postCategory)
            ? postCategory[0]
            : postCategory;
          seoMetaComposerRequestData = prepareParamsForSeoMetaComposerRequest(
            projectnetProject,
            postCategoryNormalized,
          );

          // Fetch SEO Meta Composer data
          try {
            seoMetaComposerData = await fetchSeoMetaComposerData(
              decodeURIComponent(asPath),
              seoMetaComposerRequestData,
            );
          } catch (e) {
            logger.logError('Error when fetching SEO Meta Composer data:', e);
          }
        } catch (e) {
          logger.logError(
            'Error when get post detail in content page component:',
            e,
          );
        }
      }

      if (status === 'private' && !isNil(res)) {
        res.redirect(HTTP_RESPONSE_CODES.TemporarilyMoved, config.baseUrl);
      }
      const postUrlInfo = UrlHelper.getPostUrlInfo(
        {
          propertyType,
          id,
          title,
          videoType,
          link,
        },
        asPath,
      );
      if (!postUrlInfo.isValid) {
        res.redirect(
          HTTP_RESPONSE_CODES.PermanentlyMoved,
          postUrlInfo.actualUrl + UrlHelper.getQueryStringFromUrl(asPath),
        );
      }
      store.dispatch(
        updateCurrentArticle({
          categories,
          tags,
          id,
          title,
          postCategory,
          articleTypeInGA,
        }),
      );
      if (!isNull(project.id)) {
        const {
          id: propertyId,
          name,
          developer,
          location,
          transportations,
          priceTypes,
        } = project;
        const propertyType = !isNull(project.propertyType)
          ? isArray(project.propertyType)
            ? project.propertyType.join(',')
            : toString(project.propertyType)
          : '';
        store.dispatch(
          updateCurrentProject({
            id: propertyId,
            title: name,
            developer,
            propertyType,
            location,
            transportations,
            priceTypes,
          }),
        );

        try {
          const projectRelatedVariable = {
            projectId: project.id,
            excludeId: parseInt(articleId),
          };
          projectRelatedPostData = await fetchProjectArticles(
            apolloClient,
            projectRelatedVariable,
          );
        } catch (e) {
          logger.logError(
            'Error when get post detail in content page component:',
            e,
          );
        }
      }

      const [otherPreviewPosts, recommendedVideos, readNextPosts] =
        await Promise.allSettled([
          fetchOtherPreviewPosts(apolloClient, {
            postId: parseInt(articleId),
            propertyType: postData.post.propertyType,
            postCategory: getPostCategoryParams(postData.post.postCategory),
          }),
          fetchRecommendedVideos(apolloClient, {
            id: parseInt(articleId),
          }),
          fetchReadNextPosts(apolloClient, {
            postId: parseInt(articleId),
            offset: 3,
            perPage: 5,
          }),
        ]);
      postOtherPreviewData = resolved(otherPreviewPosts);
      postRecommendedData = resolved(recommendedVideos);
      postWhatToReadData = resolved(readNextPosts);
    }
  }

  return {
    asPath,
    canonicalLink,
    post: postData?.post,
    projectRelatedPostData,
    postOtherPreviewData,
    postRecommendedData,
    postWhatToReadData,
    projectnetProjectData,
    projectNetProjectHref,
    seoMetaComposerData,
  };
};

export default ContentPage;
